import { useCallback, useEffect, useMemo, useState } from 'react'

import { useConnect, useReduxBundlerStore } from 'redux-bundler-hook'

import { CloseOutlined, Edit } from '@mui/icons-material'
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'

import { useTheme } from '@emotion/react'
import { titleize } from 'inflection'

import { DynamicSelect, FreeSoloDynamicSelect } from '@common/components/Selects'
import { getApiFetch, parseApiErrors } from '@common/utils'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {Object} [props.instance]
 * @param {string} [props.instance.id]
 * @param {string} [props.instance.zone]
 * @param {string} [props.instance.zoneName]
 * @param {string} [props.instance.unit]
 * @param {string} [props.instance.mainMac]
 */
export default function ReassignForm({ open, onClose, instance = {} }) {
  const { doDeviceAssign, doShowSnackbar } = useConnect(
    'doDeviceAssign',
    'doShowSnackbar',
  )

  const [loading, setLoading] = useState(false)
  const [unitStepLoading, setUnitStepLoading] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [selectedData, setSelectedData] = useState({
    property: null,
    unit: null,
    zone: null,
  })

  const store = useReduxBundlerStore()
  const apiFetch = getApiFetch(store)

  const handleSave = async () => {
    try {
      setLoading(true)

      const { property, zone } = selectedData
      let { unit } = selectedData
      if (!unit.id) {
        const newUnitResponse = await apiFetch(
          '/units/',
          { property: property.id, name: unit.name },
          {
            method: 'POST',
            cancelationPrefix: 'device_assign_create_unit',
          },
        )
        unit = newUnitResponse
      }

      const assignResult = await doDeviceAssign({
        id: instance.id,
        unit: unit.id,
        zone: !zone.id ? '' : zone.id,
        zoneName: !zone.id ? zone.name : '',
      })
      if (assignResult?.error) throw assignResult.error
      doShowSnackbar('Device successfully reassigned', 'success')
      onClose(true)
    } catch (err) {
      const parsedError = parseApiErrors(err?.response)
      doShowSnackbar(parsedError, 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && instance) {
      setSelectedData({
        property: instance.propertyId
          ? { id: instance.propertyId, name: instance.propertyName }
          : null,
        unit: instance.unitId ? { id: instance.unitId, name: instance.unitName } : null,
        zone: instance.zone ? { id: instance.zone, name: instance.zoneName } : null,
      })
    } else {
      setSelectedData({ property: null, unit: null, zone: null })
      setActiveStep(0)
    }
  }, [open, instance])

  const handleUnitSelect = async (_, unit) => {
    if (!unit.id) {
      setSelectedData((prev) => ({ ...prev, unit, zone: unit }))
    } else {
      try {
        setUnitStepLoading(true)

        const zones = await apiFetch(
          '/zones/',
          { active: true, unit: unit.id, search: unit.name },
          { method: 'GET', cancelationPrefix: 'device_assign_zones' },
        )

        setSelectedData((prev) => ({
          ...prev,
          unit,
          zone: zones?.results?.find((zone) => zone.name === unit.name) ?? {
            id: null,
            name: unit.name,
          },
        }))
      } catch (err) {
        setSelectedData((prev) => ({ ...prev, unit, zone: null }))
      } finally {
        setUnitStepLoading(false)
      }
    }
  }

  const steps = [
    {
      label: 'Select property',
      canProceed: selectedData.property,
      content: (
        <DynamicSelect
          getFullEntity
          size="small"
          variant="standard"
          name="property"
          endpoint="properties"
          value={selectedData.property}
          onChange={(property) =>
            setSelectedData(() => ({ property, unit: null, zone: null }))
          }
        />
      ),
    },
    {
      label: 'Select Unit',
      canProceed: selectedData.unit,
      loading: unitStepLoading,
      content: (
        <FreeSoloDynamicSelect
          getFullEntity
          size="small"
          name="unit"
          endpoint="units"
          value={selectedData.unit}
          onChange={handleUnitSelect}
          filters={{ property: selectedData.property?.id }}
          secondaryTextAttr="propertyName"
        />
      ),
    },
    {
      label: 'Select Zone',
      canProceed: selectedData.zone,
      content: (
        <FreeSoloDynamicSelect
          getFullEntity
          size="small"
          name="zone"
          endpoint="zones"
          disableOptions={!selectedData.unit?.id}
          value={selectedData.zone}
          onChange={(_, zone) => setSelectedData((prev) => ({ ...prev, zone }))}
          filters={{ unit: selectedData.unit?.id }}
        />
      ),
    },
  ]

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const getStepSublabel = (index) => {
    let sublabel
    if (index === 0 && activeStep !== 0) {
      sublabel = selectedData.property?.name
    } else if (index === 1 && activeStep !== 1) {
      sublabel = selectedData.unit?.name
    } else if (index === 2 && activeStep !== 2) {
      sublabel = selectedData.zone?.name
    }
    return sublabel ? <Typography variant="caption">{sublabel || ''}</Typography> : null
  }

  const SummaryRow = useCallback(
    ({ type, initial, current }) => {
      const currentName = current?.name
      return (
        <Typography variant="body2">
          <Box component="span" display="inline-flex" fontWeight={500}>
            {titleize(type)}
          </Box>
          : {`${initial || '--'} → `}
          <Box component="span" display="inline-flex" fontWeight={500}>
            {currentName ?? '--'}
          </Box>
          {!current?.id && (
            <Box component="span" display="inline-flex" color="red">
              *
            </Box>
          )}
        </Typography>
      )
    },
    [selectedData],
  )

  const theme = useTheme()

  const haveNonExistingEntities = useMemo(
    () => !selectedData.unit?.id || !selectedData.zone?.id,
    [selectedData],
  )

  const isDirty = useMemo(
    () =>
      instance?.propertyId !== selectedData.property?.id ||
      instance?.unitId !== selectedData.unit?.id ||
      instance.zone !== selectedData.zone?.id,
    [instance, selectedData],
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', pt: 3, pb: 0, px: 4 }}
      >
        Reassign Device
        <IconButton disabled={loading} onClick={onClose}>
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ mt: 2.5, px: 4 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel optional={getStepSublabel(index)}>
                {step.label}
                {activeStep > index && (
                  <Edit
                    sx={{
                      ml: 1,
                      fontSize: 14,
                      color: loading ? 'grey' : 'primary.main',
                      cursor: loading ? 'auto' : 'pointer',
                    }}
                    onClick={loading ? null : () => setActiveStep(index)}
                  />
                )}
              </StepLabel>
              <StepContent>
                {step.content}
                <Typography>{step.description}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'end', mt: 1 }}>
                  {index !== 0 && (
                    <Button
                      disabled={step.loading}
                      size="small"
                      onClick={handleBack}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Back
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleNext}
                    loading={step.loading}
                    disabled={!step.canProceed || step.loading}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Continue
                  </Button>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateRows: activeStep > 2 ? '1fr' : '0fr',
            transition: theme.transitions.create('grid-template-rows', {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <Box sx={{ overflow: 'hidden' }}>
            <Box
              display="flex"
              flexDirection="column"
              gap={0.5}
              mt={2}
              sx={{
                border: 1,
                borderColor: alpha('#000000', 0.1),
                borderRadius: 1,
                p: 1,
              }}
            >
              {instance?.propertyId !== selectedData.property?.id && (
                <SummaryRow
                  type="property"
                  initial={instance?.propertyName}
                  current={selectedData.property}
                />
              )}
              {(instance?.unitId !== selectedData.unit?.id ||
                !selectedData.unit?.id) && (
                <SummaryRow
                  type="unit"
                  initial={instance?.unitName}
                  current={selectedData.unit}
                />
              )}
              {(instance.zone !== selectedData.zone?.id || !selectedData.zone?.id) && (
                <SummaryRow
                  type="zone"
                  initial={instance?.zoneName}
                  current={selectedData.zone}
                />
              )}
              {!isDirty && <Typography variant="body2">No Changes Detected</Typography>}
              {haveNonExistingEntities && (
                <Typography variant="caption" mt={1}>
                  <Box display="inline-flex" color="red" fontSize={18}>
                    *
                  </Box>{' '}
                  - entity doesn&apos;t exist and will be created
                </Typography>
              )}
            </Box>
            <DialogActions sx={{ mt: 1, overflow: 'hidden' }}>
              <Button
                size="small"
                disabled={loading}
                onClick={() => setActiveStep((prev) => prev - 1)}
              >
                Back
              </Button>
              <Button
                size="small"
                disabled={loading}
                variant="contained"
                onClick={handleSave}
              >
                Save
              </Button>
            </DialogActions>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
